import { CloseCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { Badge, Button } from 'antd';
import { useFormikContext } from 'formik';
import { type ChangeEvent, useCallback, useContext, useEffect, useRef, useState } from 'react';

import './VendorLeadStepPictures.scss';

import { downloadFileApi } from 'src/app/services/DownloadFile/DownloadFile.service';
import AddPhotoIcon from 'src/assets/add-photo.svg?react';
import { ValidationError } from 'src/components/base';
import type { IVendorLeadProductImagesFormValues } from 'src/components/vendors/steps/Pictures/VendorLeadStepPictures.validation';
import { blobToBase64 } from 'src/helpers';
import { removeBase64Prefix } from 'src/helpers/removeBase64Prefix';
import { VendorsContext } from 'src/pages/VendorsPage/VendorsPage.context';
import type { IFilePayload } from 'src/types';

const pictureSrcCache: Record<string, string> = {};

type IFilePayloadWithSrc = IFilePayload & {
  src: string;
};

type ImagePreviewProps = {
  src?: string | null;
  id?: string | null;
  num: number;
  onRemove: (num: number) => void;
};

function ImagePreview({ src, id, num, onRemove }: Readonly<ImagePreviewProps>) {
  const handleRemove = useCallback(() => {
    onRemove(num);
  }, [onRemove, num]);

  return (
    <Badge
      count={
        id ? undefined : (
          <Button
            className="vendor-lead-step-pictures__image-remove-icon"
            type="primary"
            shape="circle"
            onClick={handleRemove}
            icon={<CloseCircleOutlined style={{ fontSize: 16 }} />}
          />
        )
      }
    >
      <div className="vendor-lead-step-pictures__image-preview">
        {src ? <img src={src} alt="" /> : <LoadingOutlined style={{ fontSize: 16 }} spin={true} />}
      </div>
    </Badge>
  );
}

export function VendorLeadStepPicturesImageList() {
  const { onEditing } = useContext(VendorsContext);

  const fileRef = useRef<HTMLInputElement>(null);
  const { dirty, initialValues, values, errors, setFieldValue, setFieldTouched } =
    useFormikContext<IVendorLeadProductImagesFormValues>();

  const [pictureSrcs, setPictureSrcs] = useState<string[]>([]);

  const reset = useCallback(async () => {
    if (initialValues.images.length > 0) {
      const defaultImageSrcs: string[] = (
        await Promise.all(
          initialValues.images.map(async (defaultImage) => {
            if (!defaultImage.uid) {
              return undefined;
            }

            if (defaultImage.uid && pictureSrcCache[defaultImage.uid]) {
              return pictureSrcCache[defaultImage.uid];
            }

            const imageBlob = await downloadFileApi.downloadFile(defaultImage.uid);

            const imageData = await blobToBase64(imageBlob, true);

            if (imageData) {
              if (defaultImage.uid) {
                pictureSrcCache[defaultImage.uid] = imageData;
              }

              return imageData;
            }
          })
        )
      ).filter(Boolean) as string[];

      setPictureSrcs(defaultImageSrcs);
    } else {
      setPictureSrcs([]);
    }
  }, [initialValues.images]);

  const handleFilesSelect = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      if (!event.target.files) {
        return;
      }

      const base64Files: IFilePayloadWithSrc[] = (
        await Promise.all(
          [...event.target.files].map(async (file) => {
            if (file) {
              const base64Image = await blobToBase64(file, true);

              if (base64Image) {
                return {
                  fileName: file.name,
                  contentType: file.type,
                  content: removeBase64Prefix(base64Image),
                  src: base64Image,
                };
              }
            }

            return null;
          })
        )
      ).filter(Boolean) as IFilePayloadWithSrc[];

      await setFieldValue(
        'images',
        [
          ...base64Files.map((base64File) => ({
            fileName: base64File.fileName,
            contentType: base64File.contentType,
            content: base64File.content,
          })),
          ...values.images,
        ],
        true
      );

      await setFieldTouched('images', true);

      setPictureSrcs((prev) => [...base64Files.map((base64File) => base64File.src), ...prev]);
    },
    [setFieldValue, setFieldTouched, values.images]
  );

  const handleFileRemove = useCallback(
    async (num: number) => {
      const newImages = values.images.filter((_src, index) => index !== num);

      setPictureSrcs((prev) => prev.filter((_src, index) => index !== num));

      await setFieldValue('images', newImages);
      await setFieldTouched('images', true);
    },
    [values.images, setFieldValue, setFieldTouched]
  );

  const handleImageUploadRequest = useCallback(() => fileRef.current?.click(), []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: false
  useEffect(() => {
    void reset();
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: false
  useEffect(() => {
    onEditing(dirty);
  }, [dirty]);

  return (
    <div className="vendor-lead-step-pictures__container">
      <input
        ref={fileRef}
        type="file"
        // application/octet-stream is needed to allow choice between gallery and camera on mobile phones
        accept="
          application/octet-stream,
          image/bmp,
          image/gif,
          image/jpeg,
          image/jpeg,
          image/png,
          image/tiff,
          image/vnd.wap.wbmp,
          image/webp,
          image/svg+xml
        "
        hidden={true}
        multiple={true}
        onChange={handleFilesSelect}
      />
      <div className="vendor-lead-step-pictures__title">Product photos</div>
      <div className="vendor-lead-step-pictures__controls">
        <div className="vendor-lead-step-pictures__add-photo__button flex-1" onClick={handleImageUploadRequest}>
          <AddPhotoIcon style={{ fill: '#0082F9', width: 48, height: 48 }} />
          <span className="vendor-lead-step-pictures__add-photo__text">Add Photo</span>
        </div>

        <div className="flex-3 flex column nowrap gap-small">
          <div className="flex row nowrap align-items-center gap-medium">
            <div className="vendor-lead-step-pictures__info-icon">i</div>
            <div className="vendor-lead-step-pictures__info-text">Click on add photo files here</div>
          </div>

          <div className="flex row nowrap align-items-center gap-medium">
            <div className="vendor-lead-step-pictures__info-icon">i</div>
            <div className="vendor-lead-step-pictures__info-text">
              You can manage your uploaded photo before finishing request
            </div>
          </div>

          <div className="flex row nowrap align-items-center gap-medium">
            <div className="vendor-lead-step-pictures__info-icon">i</div>
            <div className="vendor-lead-step-pictures__info-text">Maximum limit can be up to 100Mb</div>
          </div>
        </div>
      </div>

      <div className="vendor-lead-step-pictures__title">Uploaded photos ({values.images.length})</div>
      {values.images.length > 0 ? (
        <div className="vendor-lead-step-pictures__images-preview">
          {values.images.map((image, num) => (
            <ImagePreview
              key={image.fileName}
              src={pictureSrcs[num] ?? (image.uid ? pictureSrcCache[image.uid] : undefined)}
              id={image.uid}
              num={num}
              onRemove={handleFileRemove}
            />
          ))}
        </div>
      ) : errors.images ? (
        <div className="vendor-lead-step-pictures__error">
          <ValidationError name="images" errorType="danger" className="vendor-lead-step-pictures__error-message" />
        </div>
      ) : null}
    </div>
  );
}
